/* ================================================================
   DEMO-CETESB-GAC-001 RF-02 — parser puro de `?training=<id>[&lesson=<id>]`
   ================================================================

   Mesmo contrato do `_tourDeepLinkPure.ts` (TOUR-DEEP-LINK-001), porque o
   problema é o mesmo: um link colado por alguém tem de abrir a tela certa ou
   dizer com todas as letras por que não abriu — nunca derrubar o boot nem
   abrir "quase certo" em silêncio.

   Duas diferenças em relação ao de tour, ambas medidas na apresentação da
   CETESB (60 minutos cronometrados):

   1. O `lesson` é OPCIONAL e só faz sentido acompanhado de `training`. Um link
      com `?lesson=` sozinho é erro explícito, não um "abre o catálogo" — quem
      colou queria um bloco específico e precisa saber que o link está quebrado.
   2. `training` sem `lesson` é sucesso: abre o curso no começo. É o link que
      vai no convite da sessão.
   ================================================================ */

/** kebab-case: começa por letra, sem underscore, sem acento, sem barra. */
export const TRAINING_ID_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/** Teto de tamanho: id de curso/lição do seed nunca chega perto disso. */
export const TRAINING_ID_MAX_LENGTH = 64;

export interface TrainingParseSuccess {
    readonly ok: true;
    readonly trainingId: string;
    /** null quando o link aponta para o curso inteiro. */
    readonly lessonId: string | null;
}

export interface TrainingParseFailure {
    readonly ok: false;
    readonly reason:
        | 'no-training-param'
        | 'malformed-training-id'
        | 'malformed-lesson-id'
        | 'lesson-without-training'
        | 'duplicate-parameter';
    readonly detail: string;
    readonly attemptedTrainingId: string | null;
    readonly attemptedLessonId: string | null;
}

export type TrainingParseResult = TrainingParseSuccess | TrainingParseFailure;

function _amostra(s: string): string {
    return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

function _falha(
    reason: TrainingParseFailure['reason'],
    detail: string,
    training: string | null,
    lesson: string | null,
): TrainingParseFailure {
    return Object.freeze({
        ok: false as const,
        reason,
        detail,
        attemptedTrainingId: training === null ? null : _amostra(training),
        attemptedLessonId: lesson === null ? null : _amostra(lesson),
    });
}

function _idInvalido(id: string, rotulo: string): string | null {
    if (id.length > TRAINING_ID_MAX_LENGTH) return `id de ${rotulo} excede ${TRAINING_ID_MAX_LENGTH} caracteres`;
    if (!TRAINING_ID_PATTERN.test(id)) return `id de ${rotulo} fora do formato kebab-case`;
    return null;
}

/**
 * Lê `?training=` e `?lesson=` de uma query string.
 *
 * Sem `?training=` devolve `no-training-param` — o caso normal de todo boot, que
 * o dispatcher trata como silêncio e não como erro.
 */
export function parseTrainingParam(queryString: string | null | undefined): TrainingParseResult {
    if (typeof queryString !== 'string') {
        return _falha('no-training-param', 'queryString ausente', null, null);
    }

    const q = queryString.startsWith('?') ? queryString.slice(1) : queryString;
    const params = new URLSearchParams(q);
    const rawTraining = params.get('training');
    const rawLesson = params.get('lesson');

    if (params.getAll('training').length > 1 || params.getAll('lesson').length > 1) {
        return _falha(
            'duplicate-parameter',
            'curso ou bloco repetido no link; informe cada parâmetro uma única vez',
            rawTraining,
            rawLesson,
        );
    }
    if (rawTraining !== null && rawTraining.trim() === '') {
        return _falha('malformed-training-id', 'curso vazio no link', rawTraining, rawLesson);
    }
    if (rawLesson !== null && rawLesson.trim() === '') {
        return _falha('malformed-lesson-id', 'bloco vazio no link', rawTraining, rawLesson);
    }

    const temLesson = rawLesson !== null && rawLesson.trim() !== '';

    if (rawTraining === null || rawTraining.trim() === '') {
        // `?lesson=` sozinho NÃO é silêncio: o link está pela metade e quem o
        // abriu esperava um bloco específico da apresentação.
        if (temLesson) {
            return _falha(
                'lesson-without-training',
                'parâmetro ?lesson= exige ?training= no mesmo link',
                null,
                (rawLesson as string).trim(),
            );
        }
        return _falha('no-training-param', 'parâmetro ?training= ausente', null, null);
    }

    const trainingId = rawTraining.trim();
    const erroTraining = _idInvalido(trainingId, 'treinamento');
    if (erroTraining)
        return _falha(
            'malformed-training-id',
            erroTraining,
            trainingId,
            temLesson ? (rawLesson as string).trim() : null,
        );

    if (!temLesson) {
        return Object.freeze({ ok: true as const, trainingId, lessonId: null });
    }

    const lessonId = (rawLesson as string).trim();
    const erroLesson = _idInvalido(lessonId, 'lição');
    if (erroLesson) return _falha('malformed-lesson-id', erroLesson, trainingId, lessonId);

    return Object.freeze({ ok: true as const, trainingId, lessonId });
}

/**
 * Monta o link da apresentação (ou de um bloco). Usado pela documentação e por
 * quem for colar o endereço no convite da sessão.
 */
export function buildTrainingDeepLink(origin: string, trainingId: string, lessonId?: string | null): string {
    const base = String(origin || '').replace(/\/+$/, '');
    const qs = new URLSearchParams({ training: trainingId });
    if (lessonId) qs.set('lesson', lessonId);
    return `${base}/?${qs.toString()}`;
}
